<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTasksStore } from 'dashboard/stores/tasks';
import ContactAPI from 'dashboard/api/contacts';
import { debounce } from '@chatwoot/utils';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TaskStatusSelect from './TaskStatusSelect.vue';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();
const tasksStore = useTasksStore();
const agentsList = useMapGetter('agents/getAgents');

const dialogRef = ref(null);

const selectedContact = ref(null);
const contactSearchValue = ref('');
const contactResults = ref([]);

const taskDescription = ref('');
const taskDueAt = ref('');
const taskStatus = ref('todo');
const shouldAssign = ref(false);
const taskAssigneeId = ref('');
const isCreating = ref(false);

const resetForm = () => {
  selectedContact.value = null;
  contactSearchValue.value = '';
  contactResults.value = [];
  taskDescription.value = '';
  taskDueAt.value = '';
  taskStatus.value = 'todo';
  shouldAssign.value = false;
  taskAssigneeId.value = '';
};

// `status` lets a kanban column open the form already pointing at itself.
const open = ({ status } = {}) => {
  resetForm();
  if (status) taskStatus.value = status;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });

const searchContacts = debounce(async query => {
  if (!query) {
    contactResults.value = [];
    return;
  }
  const { data } = await ContactAPI.search(query);
  contactResults.value = data.payload || [];
}, 300);

const onContactSearchInput = event => {
  contactSearchValue.value = event.target.value;
  searchContacts(contactSearchValue.value);
};

const onSelectContact = contact => {
  selectedContact.value = contact;
  contactResults.value = [];
  contactSearchValue.value = '';
};

const clearContact = () => {
  selectedContact.value = null;
};

const assigneeOptions = computed(() =>
  (agentsList.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  }))
);

const assigneeLabel = computed(
  () =>
    assigneeOptions.value.find(option => option.value === taskAssigneeId.value)
      ?.label || t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGNEE_LABEL')
);

const onToggleAssign = () => {
  shouldAssign.value = !shouldAssign.value;
  if (!shouldAssign.value) {
    taskAssigneeId.value = '';
  }
};

const canSave = computed(
  () => !!selectedContact.value && !!taskDescription.value.trim()
);

const onSave = async () => {
  if (!selectedContact.value) {
    useAlert(t('TASKS_DASHBOARD.CREATE.CONTACT_REQUIRED'));
    return;
  }
  if (!taskDescription.value.trim()) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.VALIDATION_ERROR'));
    return;
  }

  isCreating.value = true;
  try {
    await tasksStore.create({
      contact_id: selectedContact.value.id,
      description: taskDescription.value.trim(),
      due_at: taskDueAt.value
        ? new Date(taskDueAt.value).toISOString()
        : null,
      status: taskStatus.value,
      assignee_id: shouldAssign.value ? taskAssigneeId.value || null : null,
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.CREATE_SUCCESS'));
    emit('created');
    close();
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.ERROR_MESSAGE'));
  } finally {
    isCreating.value = false;
  }
};

onMounted(() => {
  if (!agentsList.value?.length) {
    store.dispatch('agents/get');
  }
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('TASKS_DASHBOARD.CREATE.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex w-full flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TASKS_DASHBOARD.FILTER.CONTACT') }}
        </label>
        <div v-if="selectedContact" class="flex items-center justify-between">
          <span class="text-sm text-n-slate-11 truncate">
            {{ selectedContact.name }}
          </span>
          <NextButton
            icon="i-lucide-x"
            size="xs"
            variant="ghost"
            color="slate"
            type="button"
            @click="clearContact"
          />
        </div>
        <div v-else class="relative">
          <Input
            :model-value="contactSearchValue"
            type="search"
            :placeholder="t('TASKS_DASHBOARD.FILTER.CONTACT_PLACEHOLDER')"
            class="w-full"
            @input="onContactSearchInput"
          />
          <div
            v-if="contactResults.length"
            class="absolute top-full mt-1 w-full flex flex-col gap-0.5 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-lg p-1 z-30 max-h-40 overflow-y-auto"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="text-left text-sm px-2 py-1 rounded-md hover:bg-n-alpha-2 text-n-slate-12"
              @click="onSelectContact(contact)"
            >
              {{ contact.name }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DESCRIPTION_LABEL') }}
        </label>
        <textarea
          v-model="taskDescription"
          rows="3"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 resize-none"
          :placeholder="
            t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DESCRIPTION_PLACEHOLDER')
          "
        />
      </div>

      <div class="flex items-center gap-3">
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DUE_AT_LABEL') }}
          </label>
          <input
            v-model="taskDueAt"
            type="datetime-local"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TASKS_DASHBOARD.SORT_BY.OPTIONS.STATUS') }}
          </label>
          <div class="py-1.5">
            <TaskStatusSelect v-model="taskStatus" />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label
          class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer w-fit"
        >
          <input
            type="checkbox"
            class="h-3 w-3"
            :checked="shouldAssign"
            @change="onToggleAssign"
          />
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGN_TOGGLE_LABEL') }}
        </label>
        <div v-if="shouldAssign" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGNEE_LABEL') }}
          </label>
          <SelectMenu
            v-model="taskAssigneeId"
            :options="assigneeOptions"
            :label="assigneeLabel"
          />
        </div>
      </div>

      <div class="flex items-center justify-end gap-3">
        <NextButton
          faded
          slate
          type="button"
          :label="t('TASKS_DASHBOARD.EDIT.CANCEL')"
          @click="close"
        />
        <NextButton
          solid
          :label="t('CONTACTS_LAYOUT.SIDEBAR.TASKS.SAVE')"
          :is-loading="isCreating"
          :disabled="!canSave || isCreating"
          @click="onSave"
        />
      </div>
    </div>
  </Dialog>
</template>
